import { Button, Message } from '@alifd/next';
import { Box } from '@mui/material';
import {
  Cascader,
  DatePicker,
  Select,
  Space,
  Table,
  TableColumnsType,
  Typography,
} from 'antd';
import locale from 'antd/es/date-picker/locale/zh_TW';
import axios from 'axios';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';

import DownloadButton from '@/components/UI/DownloadButton';
import {
  GetPersonalFinanceListRequestBody as Body,
  GetPersonalFinanceListResponseData as ResponseData,
} from '@/types/api/finance/getPersonalFinanceList';
import { generateHouseHoldNumberCascader } from '@/utils/generateHouseHoldNumberCascader';

import { ListItem, Option as CascaderOption } from '../types';
import { formatNumberWithComma, getHouseHoldNumber } from '../utils';
import FinanceListDialog from './components/FinanceListDialog';

const { Option } = Select;

const PaymentList: React.FC<{
  trigger: Record<string, never>;
  setTrigger: React.Dispatch<React.SetStateAction<Record<string, never>>>;
}> = ({ trigger, setTrigger }) => {
  const [houseHoldNumber, setHouseHoldNumber] =
    useState<Body['householdNumber']>();
  // request body
  const [status, setStatus] = useState<Body['status']>();
  const [period, setPeriod] = useState<Body['period']>();
  // response data
  const [responseData, setResponseData] = useState<ResponseData>();
  // table dataSource
  const [dataSource, setDataSource] = useState<ListItem[]>([]);
  const [houseHoldNumberCascader, setHouseHoldNumberCascader] = useState<
    CascaderOption[]
  >([]);

  const getPaymentData = useCallback(async (params: Body) => {
    console.log(params);
    try {
      const res = await axios.get('/ezcon/api/bills', {
        params: {
          ...params,
        },
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
      });
      setResponseData(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const handlePay = async (id: string) => {
    Message.loading({ content: '請稍後...', duration: 0 });
    try {
      await axios.patch(
        '/ezcon/api/bill/completePayment',
        {},
        {
          headers: {
            authorization: localStorage.getItem('feathers-jwt'),
          },
          params: {
            id,
          },
        }
      );
      setTimeout(() => {
        Message.success('繳費成功');
        setTrigger({});
      }, 1000);
    } catch (err) {
      Message.error('繳費失敗');
      console.error(err);
    }
  };

  const handleCancelPay = async (id: string) => {
    Message.loading({ content: '處理中...', duration: 0 });
    try {
      await axios.patch(
        '/ezcon/api/bill/cancelPayment',
        {},
        {
          headers: {
            authorization: localStorage.getItem('feathers-jwt'),
          },
          params: {
            id,
          },
        }
      );
      setTimeout(() => {
        Message.success('取消繳費成功');
        setTrigger({});
      }, 1000);
    } catch (err) {
      Message.error('取消繳費失敗');
      console.error(err);
    }
  };

  const handleDeletePay = async (id: string) => {
    const result = await Swal.fire({
      title: '確定要刪除嗎？',
      text: '刪除後將無法復原',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '確定',
      cancelButtonText: '取消',
    });
    if (result.isConfirmed) {
      Message.loading({ content: '處理中...', duration: 0 });
      try {
        await axios.delete('/ezcon/api/bill', {
          headers: {
            authorization: localStorage.getItem('feathers-jwt'),
          },
          params: {
            id,
          },
        });
        setTimeout(() => {
          Message.success('刪除成功');
          setTrigger({});
        }, 1000);
      } catch (err) {
        Message.error('刪除失敗');
        console.error(err);
      }
    }
  };

  useEffect(() => {
    const newBody: Body = {};
    if (houseHoldNumber) {
      newBody.householdNumber = houseHoldNumber;
    }
    if (status !== undefined) {
      newBody.status = status;
    }
    if (period) {
      newBody.period = period;
    }
    getPaymentData(newBody);
  }, [getPaymentData, houseHoldNumber, period, status, trigger]);

  useEffect(() => {
    (async () =>
      setHouseHoldNumberCascader(
        generateHouseHoldNumberCascader(await getHouseHoldNumber())
      ))();
  }, []);

  useEffect(() => {
    if (responseData) {
      console.log(responseData);
      // const newDataSource: ListItem[] = responseData.map((data) => ({
      //   ...data,
      //   // key: data._id,
      //   // FinanceList: data.financeList.map((finance) => ({
      //   //   ...finance,
      //   //   paymentPeriod: finance.paymentPeriod,
      //   //   title: finance.title,
      //   //   money: finance.money,
      //   // })),
      // }));
      setDataSource(responseData);
    }
  }, [responseData]);

  const columns: TableColumnsType<ListItem> = [
    {
      title: '帳單期數',
      dataIndex: 'itemName',
      key: 'itemName',
      defaultSortOrder: 'descend',
      sorter: {
        compare: (a, b) => a.itemName.localeCompare(b.itemName),
        multiple: 2,
      },
    },
    {
      title: '戶號',
      key: 'householdNumber',
      dataIndex: 'householdNumber',
      defaultSortOrder: 'ascend',
      sorter: {
        compare: (a, b) => a.householdNumber.localeCompare(b.householdNumber),
        multiple: 1,
      },
    },
    {
      title: '金額',
      key: 'money',
      dataIndex: 'money',
      sorter: (a, b) => a.money - b.money,
      render: (value: number) => {
        const formattedNumber = formatNumberWithComma(value.toString());
        return formattedNumber;
      },
    },
    {
      title: '狀態',
      key: 'status',
      dataIndex: 'status',
      render: (val) => {
        const statusMap = {
          0: '未繳費',
          1: '已繳費',
          2: '已過期',
          3: '未入帳',
        };
        return statusMap[val];
      },
    },
    {
      title: '操作',
      key: 'x',
      dataIndex: '_id',
      render: (id, record) => {
        return (
          <Space>
            <FinanceListDialog
              payTitle={record.itemName}
              // financeList ={record.FinanceList}
              // financeList={[]}
              setTrigger={setTrigger}
              financeId={record._id}
            />

            {record.status !== 3 && (
              <DownloadButton
                url={'/ezcon/api/getBillPDF'}
                responseFileType={'blob'}
                fileName={`${record.itemName}.pdf`}
                params={{
                  timeStart: record.billTime,
                  householdNumber: record.householdNumber,
                  isFilterPaid: false,
                }}
              >
                下載
              </DownloadButton>
            )}

            {moment().isSameOrBefore(
              moment(record.expireDate)
                .add(1, 'M')
                .startOf('M')
                .add(1, 'Day')
                .format('YYYY-MM-DD')
            ) && (
              <>
                {(record.status === 0 || record.status === 2) && (
                  <Button
                    style={{ width: 100 }}
                    type="primary"
                    onClick={() => handlePay(id)}
                  >
                    繳費
                  </Button>
                )}
                {record.status === 1 && (
                  <Button
                    style={{ width: 100 }}
                    type="primary"
                    onClick={() => handleCancelPay(id)}
                  >
                    取消繳費
                  </Button>
                )}
                {record.status === 3 && (
                  <Button
                    type="primary"
                    warning
                    onClick={() => handleDeletePay(record._id)}
                  >
                    刪除
                  </Button>
                )}
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <Box>
        <Space>
          <Typography>篩選：</Typography>
          <DatePicker.RangePicker
            style={{ width: '300px' }}
            locale={locale}
            picker="month"
            placeholder={['選擇開始月份', '選擇結束月份']}
            onChange={(val) => {
              if (val) {
                setPeriod([
                  moment(val[0]).startOf('M').valueOf(),
                  moment(val[1]).endOf('M').valueOf(),
                ]);
              } else {
                setPeriod(undefined);
              }
            }}
          />
          <Cascader
            placeholder="選擇戶號"
            options={houseHoldNumberCascader}
            value={houseHoldNumber?.split('-') || []}
            onChange={(val) =>
              setHouseHoldNumber(val ? val.join('-') : undefined)
            }
          />
          <Select
            placeholder="選擇狀態"
            value={status}
            onChange={(val) => setStatus(val)}
            style={{ width: '100px' }}
            allowClear
          >
            <Option value={0}>未繳費</Option>
            <Option value={1}>已繳費</Option>
            <Option value={2}>已過期</Option>
            <Option value={3}>未入帳</Option>
          </Select>
        </Space>
      </Box>
      <Box
        sx={{
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '5px',
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'end',
        }}
      >
        <Table
          style={{ width: '100%' }}
          columns={columns}
          bordered
          dataSource={dataSource}
          showSorterTooltip={false}
          sortDirections={['descend', 'ascend', 'descend']}
        />
      </Box>
    </Box>
  );
};

export default PaymentList;
